import { gql, useQuery } from '@apollo/client';
import { TRANSLATED_STRING_FIELDS } from '@assets/i18n/config';
import { MenuItem } from '@mui/material';
import { SelectField } from '@ovComponents/2-component/selectField/selectField';
import { UserContext } from '@providers/userContextProvider';
import { kebabCase } from 'lodash-es';
import { useContext } from 'react';
import { useLocalization } from '../../../hooks/useLocalization';

const FETCH_FORM_DEFINITIONS = gql`
  query fetchFormDefinitions($input: FetchFormDefinitionsInput!) {
    fetchFormDefinitions(input: $input) {
      formDefinitions {
        id
        key
        translatedName { ${TRANSLATED_STRING_FIELDS} }
        objectType
      }
      totalCount
    }
  }
`;
type FormDefinitionSelectProp = {
  label: string;
  onChange: (id: string) => void;
  value: string | undefined;
  sx?: any;
  size?: 'small' | 'medium';
  showValue?: boolean;
  filter?: any;
  onBlur?: any;
  error?: any;
  testId?: string;
  organizationId?: string;
  onObjectTypeChange?: (objectType: string) => void;
};
export const FormDefinitionSelect = ({
  label,
  onChange,
  value,
  sx = {},
  size,
  filter,
  onBlur,
  error,
  testId,
  organizationId,
  onObjectTypeChange,
}: FormDefinitionSelectProp) => {
  const { translateBackendString, language } = useLocalization();
  const { activeOrganization } = useContext(UserContext);
  const { data } = useQuery(FETCH_FORM_DEFINITIONS, {
    variables: {
      input: {
        filter: {
          states: ['ACTIVE'],
          ...filter,
          organizationId: organizationId ?? activeOrganization.id,
        },
        pagination: {
          perPage: 1000,
          sortField: `translatedName.${language}`,
          sortDesc: false,
        },
      },
      fetchPolicy: 'cache-and-network',
    },
  });

  return (
    <SelectField
      onChange={(e: any) => {
        onChange(e.target.value);
        if (onObjectTypeChange) {
          const f = data?.fetchFormDefinitions?.formDefinitions?.find(
            (formDefinition: any) => formDefinition.key === e.target.value,
          );
          onObjectTypeChange(f.objectType);
        }
      }}
      label={label}
      fullWidth
      sx={sx}
      size={size}
      value={value ?? ''}
      onBlur={onBlur}
      error={error}
      testId={testId ?? 'formDefinition-select'}
    >
      {data?.fetchFormDefinitions?.formDefinitions?.map(
        (formDefinition: any) => (
          <MenuItem
            data-testId={
              testId
                ? `${testId}-${kebabCase(formDefinition.key)}`
                : `formDefinition-select-${kebabCase(formDefinition.name)}`
            }
            key={formDefinition.key}
            value={formDefinition.key}
          >
            {translateBackendString(formDefinition.translatedName)}
          </MenuItem>
        ),
      ) ?? []}
    </SelectField>
  );
};
